import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import type { ButtonProps, UploadProps } from 'antd';
import { Button, Tooltip, Upload } from 'antd';
import clx from 'classnames';
import React, { ForwardedRef } from 'react';
import styled from 'styled-components';

const StyledUploadButton = styled(Button)`
  &.ant-btn {
    &.multi-line-button {
      height: auto;
      white-space: normal;
      > span {
        display: inline;
      }
    }
  }
`;

const StyledUpload = styled(Upload)`
  width: 100%;

  .ant-upload.ant-upload-select {
    width: 100%;
    padding-bottom: 2px;
  }

  .ant-upload-list {
    .ant-upload-list-item {
      .ant-upload-list-item-actions .ant-upload-list-item-action {
        opacity: 1;
      }
    }
  }

  // quando si omette il nome del file si mostrano le immagini in linea
  &.multi-image-inline-no-name .ant-upload-list.ant-upload-list-picture {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 -8px;
    .ant-upload-list-item-container {
      .ant-upload-list-item-name {
        padding: 0 4px;
      }
    }
  }
`;

export interface UploadInputProps extends Omit<UploadProps, 'children'> {
  /**
   * Permette di sovrascrivere il messaggio principale.
   * @default 'Carica file'
   */
  title?: React.ReactNode;
  /**
   * Permette di personalizzare il pulsante di Upload.
   */
  buttonProps?: Omit<ButtonProps, 'children'>;
  /**
   * Perette di mostrare le immagini caricate in linea quando si omette il nome.
   */
  multiImageInlineNoName?: boolean;
  /**
   * Testo del tooltip. Se non è presente il tooltip non viene mostrato.
   */
  tooltip?: string;
  /**
   * Permette di avere un pulsante du upload con il testo su più righe.
   */
  multiLineButton?: boolean;
  /**
   * Ulteriori testi per sreen-reader - accessibilità.
   */
  srFieldLabel?: React.ReactNode;
}

export type UploadOptions = Parameters<
  Exclude<UploadInputProps['customRequest'], undefined>
>[0];

/**
 * Componente di upload per caricare un file.
 *
 * @param title Permette di sovrascrivere il testo del pulsante.
 */
export const UploadInput = React.forwardRef(
  (
    props: UploadInputProps,
    ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const {
      title,
      buttonProps,
      disabled,
      className,
      multiImageInlineNoName,
      tooltip,
      multiLineButton,
      srFieldLabel,
      ...uploadProps
    } = props;
    const { t } = useTranslation();

    return (
      <StyledUpload
        disabled={disabled}
        {...uploadProps}
        className={clx(className, {
          'multi-image-inline-no-name': multiImageInlineNoName,
        })}
      >
        <Tooltip title={tooltip}>
          <StyledUploadButton
            ref={ref}
            // Override del comportamento di default di `ErrorsList` che
            // effettua il focus sull'input, in quanto in questo caso è
            // nascosto (display: none)
            data-form-focusable-for={uploadProps.name}
            icon={<UploadOutlined aria-hidden="true" />}
            disabled={disabled}
            size="large"
            block
            {...buttonProps}
            className={clx(buttonProps?.className, {
              'multi-line-button': multiLineButton,
            })}
          >
            {srFieldLabel && <span className="sr-only">{srFieldLabel}. </span>}
            {title ?? t('inputs.upload-input.title', 'Carica file')}
          </StyledUploadButton>
        </Tooltip>
      </StyledUpload>
    );
  },
);

UploadInput.displayName = 'UploadInput';
